/**
 * Step3Reports — Inclusoes em Relatorios step of the RiskApplicationModal stepper.
 */

import React from 'react';
import {
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { Controller, type Control } from 'react-hook-form';
import { OCC_REPORT_TYPE_LABELS } from '../../../types/occupationalRisk';
import type { ReportType } from '../../../types/occupationalRisk';
import { REPORT_GROUPS } from '../../../types/documentTypes.generated';
import type { ReportGroup } from '../../../types/documentTypes.generated';
import type { StepperFormValues } from '../RiskApplicationModal';

interface Step3ReportsProps {
  control: Control<StepperFormValues>;
  disabled: boolean;
}

// All report types in the groups
const ALL_REPORT_TYPES: ReportType[] = REPORT_GROUPS.flatMap((g) => g.types);

interface GroupCheckboxesProps {
  group: ReportGroup;
  selected: ReportType[];
  disabled: boolean;
  onToggle: (type: ReportType) => void;
  onSelectGroup: (types: ReportType[]) => void;
  onClearGroup: (types: ReportType[]) => void;
}

const GroupCheckboxes: React.FC<GroupCheckboxesProps> = ({
  group,
  selected,
  disabled,
  onToggle,
  onSelectGroup,
  onClearGroup,
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography variant="subtitle2" color="text.primary">
        {group.label}
      </Typography>
      <Button
        type="button"
        size="small"
        sx={{ fontSize: 11, py: 0, minWidth: 0, px: 0.5 }}
        disabled={disabled}
        onClick={() => onSelectGroup(group.types)}
      >
        Todos
      </Button>
      <Button
        type="button"
        size="small"
        color="inherit"
        sx={{ fontSize: 11, py: 0, minWidth: 0, px: 0.5 }}
        disabled={disabled}
        onClick={() => onClearGroup(group.types)}
      >
        Limpar
      </Button>
    </Box>
    <FormGroup sx={{ pl: 1 }}>
      {group.types.map((type) => (
        <FormControlLabel
          key={type}
          control={
            <Checkbox
              checked={selected.includes(type)}
              onChange={() => onToggle(type)}
              disabled={disabled}
              size="small"
            />
          }
          label={OCC_REPORT_TYPE_LABELS[type]}
        />
      ))}
    </FormGroup>
  </Box>
);

export const Step3Reports: React.FC<Step3ReportsProps> = ({ control, disabled }) => (
  <Controller
    name="reportTypes"
    control={control}
    render={({ field }) => {
      const selected = (field.value as string[]) as ReportType[];

      const toggle = (type: ReportType) => {
        if (selected.includes(type)) {
          field.onChange(selected.filter((t) => t !== type));
        } else {
          field.onChange([...selected, type]);
        }
      };

      const selectGroup = (types: ReportType[]) => {
        const merged = Array.from(new Set([...selected, ...types]));
        field.onChange(merged);
      };

      const clearGroup = (types: ReportType[]) => {
        field.onChange(selected.filter((t) => !types.includes(t)));
      };

      return (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="button"
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => field.onChange([...ALL_REPORT_TYPES])}
            >
              Selecionar todos
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              color="inherit"
              disabled={disabled}
              onClick={() => field.onChange([])}
            >
              Limpar
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Selecione os relatorios e documentos que devem incluir este risco (opcional).
          </Typography>

          <Stack spacing={1.5} divider={<Divider />}>
            {REPORT_GROUPS.map((group) => (
              <GroupCheckboxes
                key={group.label}
                group={group}
                selected={selected}
                disabled={disabled}
                onToggle={toggle}
                onSelectGroup={selectGroup}
                onClearGroup={clearGroup}
              />
            ))}
          </Stack>
        </Stack>
      );
    }}
  />
);

export default Step3Reports;
